import { useState, useEffect, useRef } from "react";
import { useApp } from "../context/AppContext";
import { Icons } from "../components/Icons";

const INTENT_STYLE = {
  info: { bg: "#6366f1", label: "Інфо" },
  commercial: { bg: "#10b981", label: "Комерц." },
  unsure: { bg: "#94a3b8", label: "Невідомо" },
};

const PHASE_LABEL = {
  queued: "У черзі…",
  normalize: "Нормалізація запитів…",
  serp: "SERP-прогін через Serpstat…",
  overlap: "Побудова кластерів за перетином SERP…",
  embeddings: "Семантичне доуточнення…",
  naming: "Найменування тем (AI)…",
  done: "Готово",
};

export default function InfoBaseView() {
  const { proj, activeId, apiFetch, dark, t, card, inp, btnP, flash } = useApp();
  const [tab, setTab] = useState("queries");

  // ---- queries tab ----
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [harvesting, setHarvesting] = useState(false);
  const [onlyInfo, setOnlyInfo] = useState(false);

  const loadQueries = () => {
    if (!activeId) return;
    setLoading(true);
    const url = `/api/projects/${activeId}/infobase/queries${onlyInfo ? "?intent=info" : ""}`;
    apiFetch(url)
      .then((r) => r.json())
      .then((d) => setQueries((d && d.queries) || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadQueries();
    // eslint-disable-next-line
  }, [activeId, onlyInfo]);

  const harvest = async () => {
    setHarvesting(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/infobase/harvest`, { method: "POST" });
      const d = await r.json();
      if (d.error) {
        flash(d.error);
      } else {
        flash(`Зібрано ${d.harvested} запитів (інфо: ${d.info}, комерц.: ${d.commercial})`);
        loadQueries();
      }
    } catch (e) {
      flash("Помилка: " + e.message);
    }
    setHarvesting(false);
  };

  // ---- clusters tab ----
  const [minImp, setMinImp] = useState(10);
  const [scope, setScope] = useState("info_unsure");
  const [estimate, setEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [run, setRun] = useState(null);
  const [clusters, setClusters] = useState([]);
  const [clustersLoading, setClustersLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const pollRef = useRef(null);

  const loadClusters = () => {
    setClustersLoading(true);
    apiFetch(`/api/projects/${activeId}/infobase/clusters`)
      .then((r) => r.json())
      .then((d) => setClusters((d && d.clusters) || []))
      .catch(() => {})
      .finally(() => setClustersLoading(false));
  };

  const loadStatus = async () => {
    try {
      const r = await apiFetch(`/api/projects/${activeId}/infobase/cluster/status`);
      const d = await r.json();
      setRun(d.run || null);
      return d.run;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    if (tab !== "clusters" || !activeId) return;
    loadStatus().then((rn) => {
      if (rn && rn.status === "running") startPolling();
      loadClusters();
    });
    return () => stopPolling();
    // eslint-disable-next-line
  }, [tab, activeId]);

  const startPolling = () => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      const rn = await loadStatus();
      if (!rn || rn.status !== "running") {
        stopPolling();
        if (rn && rn.status === "done") {
          flash(`Кластеризацію завершено: ${rn.clusters_count} тем`);
          loadClusters();
        } else if (rn && rn.status === "error") {
          flash("Помилка кластеризації: " + (rn.error || ""));
        }
      }
    }, 2500);
  };
  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const doEstimate = async () => {
    setEstimating(true);
    setEstimate(null);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/infobase/cluster/estimate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ scope, minImpressions: minImp }),
      });
      const d = await r.json();
      if (d.error) flash(d.error);
      else setEstimate(d);
    } catch (e) {
      flash("Помилка: " + e.message);
    }
    setEstimating(false);
  };

  const startCluster = async () => {
    try {
      const r = await apiFetch(`/api/projects/${activeId}/infobase/cluster`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ scope, minImpressions: minImp }),
      });
      const d = await r.json();
      if (d.error) { flash(d.error); return; }
      setEstimate(null);
      flash("Кластеризацію запущено");
      await loadStatus();
      startPolling();
    } catch (e) {
      flash("Помилка: " + e.message);
    }
  };

  const th = { padding: "8px 12px", textAlign: "left", fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", borderBottom: `1px solid ${t.border}` };
  const thC = { ...th, textAlign: "center" };
  const td = { padding: "10px 12px", fontSize: 13, borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}` };
  const tdC = { ...td, textAlign: "center" };

  const tabBtn = (id, label) => (
    <button
      onClick={() => setTab(id)}
      style={{
        padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer",
        background: "transparent", border: "none",
        color: tab === id ? (dark ? "#fff" : "#111") : t.textMuted,
        borderBottom: `2px solid ${tab === id ? "#6366f1" : "transparent"}`,
      }}
    >
      {label}
    </button>
  );

  const isRunning = run && run.status === "running";
  const pct = run && run.total > 0 ? Math.round((run.processed / run.total) * 100) : 0;

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Інфо база</h2>
        <div style={{ fontSize: 12, color: t.textMuted, marginTop: 2 }}>
          Інформаційні запити з Google Search Console → теми статей {proj && proj.name ? `· ${proj.name}` : ""}
        </div>
      </div>

      <div style={{ display: "flex", gap: 4, borderBottom: `1px solid ${t.border}`, marginBottom: 16 }}>
        {tabBtn("queries", "Запити")}
        {tabBtn("clusters", "Кластери (теми)")}
      </div>

      {tab === "queries" && (
        <>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
            <button onClick={harvest} disabled={harvesting} style={{ ...btnP, opacity: harvesting ? 0.6 : 1 }}>
              {harvesting ? "Збираю..." : "Зібрати інфо-запити з консолі"}
            </button>
          </div>
          <div style={{ ...card, padding: 0, overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: `1px solid ${t.border}` }}>
              <div style={{ fontSize: 13, color: t.textMuted }}>
                {loading ? "Завантаження..." : `${queries.length} запитів`}
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                <input type="checkbox" checked={onlyInfo} onChange={(e) => setOnlyInfo(e.target.checked)} />
                Тільки інфо
              </label>
            </div>
            {!loading && queries.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: t.textMuted, fontSize: 14 }}>
                Ще немає даних. Натисніть «Зібрати інфо-запити з консолі».
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={th}>Запит</th>
                      <th style={thC}>Покази</th>
                      <th style={thC}>Кліки</th>
                      <th style={thC}>Позиція</th>
                      <th style={thC}>CTR %</th>
                      <th style={thC}>Інтент</th>
                    </tr>
                  </thead>
                  <tbody>
                    {queries.map((q, i) => {
                      const st = INTENT_STYLE[q.intent] || INTENT_STYLE.unsure;
                      return (
                        <tr key={i} style={{ background: i % 2 === 0 ? "transparent" : (dark ? "rgba(255,255,255,.02)" : "rgba(0,0,0,.015)") }}>
                          <td style={{ ...td, fontWeight: 500 }}>{q.query}</td>
                          <td style={tdC}>{Number(q.impressions || 0).toLocaleString("uk-UA")}</td>
                          <td style={tdC}>{Number(q.clicks || 0).toLocaleString("uk-UA")}</td>
                          <td style={tdC}>{Number(q.position || 0).toFixed(1)}</td>
                          <td style={tdC}>{(Number(q.ctr || 0) * 100).toFixed(1)}</td>
                          <td style={tdC}>
                            <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 700, color: "#fff", background: st.bg }}>
                              {st.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {tab === "clusters" && (
        <>
          {/* Панель запуску */}
          <div style={{ ...card, padding: 16, marginBottom: 16 }}>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 16 }}>
              <div>
                <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 4 }}>Мін. показів</div>
                <input type="number" min={0} value={minImp} onChange={(e) => setMinImp(Math.max(0, +e.target.value || 0))} style={{ ...inp, width: 100 }} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 4 }}>Які запити</div>
                <select value={scope} onChange={(e) => setScope(e.target.value)} style={{ ...inp, width: 200 }}>
                  <option value="info_unsure">Інфо + невизначені</option>
                  <option value="info">Тільки інфо</option>
                  <option value="all">Усі (з комерційними)</option>
                </select>
              </div>
              <button onClick={doEstimate} disabled={estimating || isRunning} style={{ ...btnP, background: "transparent", color: "#6366f1", border: "1px solid #6366f1", opacity: estimating || isRunning ? 0.6 : 1 }}>
                {estimating ? "Рахую…" : "Порахувати вартість"}
              </button>
            </div>

            {estimate && !isRunning && (
              <div style={{ marginTop: 16, padding: 14, borderRadius: 8, background: dark ? "rgba(99,102,241,.08)" : "rgba(99,102,241,.06)", border: "1px solid rgba(99,102,241,.25)" }}>
                <div style={{ fontSize: 13, marginBottom: 8 }}>
                  Запитів у роботі: <b>{estimate.totalQueries}</b> · унікальних після дедупу: <b>{estimate.uniqueQueries}</b> · вже в кеші: <b>{estimate.cached}</b>
                </div>
                <div style={{ fontSize: 14, marginBottom: 12 }}>
                  Потрібно <b style={{ color: "#6366f1" }}>{estimate.credits}</b> Serpstat-юнітів (1 юніт ≈ 1 запит).
                </div>
                <button onClick={startCluster} style={{ ...btnP }}>
                  Запустити кластеризацію
                </button>
              </div>
            )}
          </div>

          {/* Прогрес */}
          {isRunning && (
            <div style={{ ...card, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                {PHASE_LABEL[run.phase] || run.phase}
              </div>
              {run.phase === "serp" && run.total > 0 && (
                <>
                  <div style={{ height: 8, borderRadius: 4, background: dark ? "#1e1e2c" : "#eee", overflow: "hidden", marginBottom: 6 }}>
                    <div style={{ height: "100%", width: `${pct}%`, background: "#6366f1", transition: "width .3s" }} />
                  </div>
                  <div style={{ fontSize: 12, color: t.textMuted }}>
                    {run.processed} / {run.total} запитів · Serpstat-викликів: {run.serp_calls}
                  </div>
                </>
              )}
              {run.phase !== "serp" && (
                <div style={{ fontSize: 12, color: t.textMuted }}>Обробка…</div>
              )}
            </div>
          )}

          {/* Кластери */}
          {clustersLoading ? (
            <div style={{ padding: 40, textAlign: "center", color: t.textMuted }}>Завантаження…</div>
          ) : clusters.length === 0 ? (
            !isRunning && (
              <div style={{ ...card, padding: 40, textAlign: "center", color: t.textMuted, fontSize: 14 }}>
                Кластерів ще немає. Порахуйте вартість і запустіть кластеризацію.
              </div>
            )
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ fontSize: 13, color: t.textMuted }}>{clusters.length} тем</div>
              {clusters.map((c) => {
                const open = !!expanded[c.id];
                return (
                  <div key={c.id} style={{ ...card, padding: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 15, fontWeight: 700 }}>{c.topic}</div>
                        {c.h1 && c.h1 !== c.topic && (
                          <div style={{ fontSize: 12, color: t.textMuted, marginTop: 2 }}>H1: {c.h1}</div>
                        )}
                        {c.title && (
                          <div style={{ fontSize: 12, color: t.textMuted }}>Title: {c.title}</div>
                        )}
                        <div style={{ fontSize: 12, color: t.textMuted, marginTop: 4 }}>
                          Головний запит: <b>{c.pillar_query}</b>
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 16, textAlign: "center", flexShrink: 0 }}>
                        <div>
                          <div style={{ fontSize: 16, fontWeight: 700 }}>{c.query_count}</div>
                          <div style={{ fontSize: 10, color: t.textMuted }}>запитів</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 16, fontWeight: 700 }}>{Number(c.total_impressions || 0).toLocaleString("uk-UA")}</div>
                          <div style={{ fontSize: 10, color: t.textMuted }}>показів</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 16, fontWeight: 700 }}>{Number(c.avg_position || 0).toFixed(1)}</div>
                          <div style={{ fontSize: 10, color: t.textMuted }}>сер. поз.</div>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setExpanded((s) => ({ ...s, [c.id]: !open }))}
                      style={{ marginTop: 10, fontSize: 12, color: "#6366f1", background: "transparent", border: "none", cursor: "pointer", padding: 0 }}
                    >
                      {open ? "▲ Сховати запити" : `▼ Показати запити (${(c.queries || []).length})`}
                    </button>
                    {open && (
                      <div style={{ marginTop: 10, overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                          <thead>
                            <tr>
                              <th style={th}>Запит</th>
                              <th style={thC}>Покази</th>
                              <th style={thC}>Позиція</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(c.queries || []).map((q, i) => (
                              <tr key={i}>
                                <td style={td}>{q.query}</td>
                                <td style={tdC}>{Number(q.impressions || 0).toLocaleString("uk-UA")}</td>
                                <td style={tdC}>{Number(q.position || 0).toFixed(1)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
